/* assets/rod-rail.js : product rail controls, phone progress bar and the async product
   recommendations fetch (WP-1, SPEC 5.6.5). Loaded by snippets/rod-rail.liquid and by
   sections/rod-product-rail.liquid (needed for the skeleton, which renders no snippet). */
import { Rod } from '@rod/core';

const doc = document;
const boundTracks = new WeakSet();
const boundPending = new WeakSet();
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function controlsFor(track) {
  if (!track.id) return { prev: null, next: null };
  const scope = track.closest('.rod-rail') || doc;
  return {
    prev: scope.querySelector(`[data-rod-rail-prev][aria-controls="${track.id}"]`),
    next: scope.querySelector(`[data-rod-rail-next][aria-controls="${track.id}"]`),
  };
}

function update(track, prev, next, thumb) {
  const max = track.scrollWidth - track.clientWidth;
  const left = track.scrollLeft;
  if (prev) prev.setAttribute('aria-disabled', left <= 1 ? 'true' : 'false');
  if (next) next.setAttribute('aria-disabled', left >= max - 1 ? 'true' : 'false');
  if (thumb && track.scrollWidth > 0) {
    const ratio = Math.min(1, track.clientWidth / track.scrollWidth);
    thumb.style.width = `${Math.max(12, ratio * 100)}%`;
    const travel = max > 0 ? left / max : 0;
    thumb.style.left = `${travel * (100 - Math.max(12, ratio * 100))}%`;
  }
}

function scrollBy(track, direction) {
  track.scrollBy({
    left: direction * track.clientWidth,
    behavior: reduceMotion.matches ? 'auto' : 'smooth',
  });
}

function bindTrack(track) {
  if (boundTracks.has(track)) return;
  boundTracks.add(track);
  const { prev, next } = controlsFor(track);
  const rail = track.closest('.rod-rail');
  const thumb = rail ? rail.querySelector('.rod-rail__thumb') : null;
  const refresh = Rod.throttleRaf(() => update(track, prev, next, thumb));

  if (prev) {
    prev.addEventListener('click', () => {
      if (prev.getAttribute('aria-disabled') === 'true') return;
      scrollBy(track, -1);
    });
  }
  if (next) {
    next.addEventListener('click', () => {
      if (next.getAttribute('aria-disabled') === 'true') return;
      scrollBy(track, 1);
    });
  }
  track.addEventListener('scroll', refresh, { passive: true });
  window.addEventListener('resize', Rod.debounce(refresh, 150));
  if ('ResizeObserver' in window) new ResizeObserver(refresh).observe(track);
  update(track, prev, next, thumb);
}

function bindPending(el) {
  if (boundPending.has(el)) return;
  boundPending.add(el);
  const url = el.getAttribute('data-rod-reco-url');
  if (!url) return;

  const load = () => {
    fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const rail = parsed.querySelector('.rod-rail:not(.rod-rail--pending)');
        if (rail) {
          el.replaceWith(rail);
          bindAll();
        } else {
          removeSection(el);
        }
      })
      .catch(() => removeSection(el));
  };

  if (!('IntersectionObserver' in window)) {
    load();
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.disconnect();
        load();
      }
    },
    { rootMargin: '400px' }
  );
  observer.observe(el);
}

function removeSection(el) {
  const section = el.closest('.shopify-section') || el.closest('.rod-rail-section') || el;
  section.remove();
}

function bindAll() {
  for (const track of doc.querySelectorAll('[data-rod-rail]')) bindTrack(track);
  for (const pendingEl of doc.querySelectorAll('[data-rod-reco-url]')) bindPending(pendingEl);
}

if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', bindAll, { once: true });
else bindAll();

doc.addEventListener('shopify:section:load', bindAll);

export { bindAll };
